import React from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import type { ExecutionContext } from '../../services/complianceExecutionApi'
import wfStyles from '@/styles/pages/compliance-workflow/ComplianceWorkflow.module.scss'

interface Props {
  standardCode: string
  context: ExecutionContext | null
  onResume?: () => void
  busy?: boolean
}

export default function WorkflowResumeCard({ standardCode, context, onResume, busy }: Props) {
  const { t } = useI18n()
  const resume = context?.resume
  if (!context?.orchestrationEnabled && !resume) return null

  const stage = context?.activeStage || resume?.currentStage
  const progress = context?.progress
  const blockers = resume?.openBlockerCount ?? context?.unresolvedBlockers?.length ?? 0

  return (
    <div className={wfStyles.resumeCard}>
      <p className={wfStyles.itemTitle}>{standardCode}</p>
      {stage ? (
        <p className={wfStyles.itemMeta}>
          {t('complianceExecution.currentStage', 'Current stage')}: {stage.replace(/_/g, ' ')}
        </p>
      ) : null}
      {progress?.controlsSatisfiedLabel ? (
        <p className={wfStyles.itemMeta}>
          {t('complianceExecution.progress.label', 'Progress')}: {progress.controlsSatisfiedLabel}{' '}
          {t('complianceExecution.progress.controlsSatisfied', 'controls satisfied')}
        </p>
      ) : null}
      {blockers > 0 ? (
        <p className={wfStyles.itemMeta}>
          {t('complianceExecution.openBlockers', 'Open blockers')}: {blockers}
        </p>
      ) : null}
      {resume?.nextActions?.length ? (
        <div className={wfStyles.innerStack}>
          <p className={wfStyles.itemMeta}>{t('complianceExecution.nextActions', 'Next actions')}:</p>
          <ul className="m-0 pl-4">
            {resume.nextActions.slice(0, 5).map((a, i) => (
              <li key={i} className={wfStyles.itemMeta}>
                {a.label}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2 mt-3">
        {onResume ? (
          <Button type="button" size="small" color="brand" disabled={busy} onClick={onResume}>
            {t('complianceExecution.resume.workflow', 'Resume workflow')}
          </Button>
        ) : null}
        <Link to={`/compliance-center/assessments?standard=${encodeURIComponent(standardCode)}`}>
          <Button type="button" size="small" variant="outline">
            {t('complianceExecution.resume.assessments', 'Open assessments')}
          </Button>
        </Link>
        <Link
          to={`/compliance-center/evidence?standard=${encodeURIComponent(standardCode)}`}
        >
          <Button type="button" size="small" variant="outline">
            {t('complianceExecution.resume.evidence', 'Open evidence hub')}
          </Button>
        </Link>
      </div>
      {resume?.discoveryPersisted ? (
        <p className={wfStyles.loadingText}>
          {t('complianceExecution.discoveryRestored', 'Applicability discovery restored from session.')}
        </p>
      ) : null}
    </div>
  )
}
